import axios, { AxiosInstance, AxiosResponse } from 'axios';

// API Configuration
const API_BASE_URL = '/api/v1';

// Create axios instance
export const apiClient: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json; charset=utf-8',
    'Accept': 'application/json; charset=utf-8',
  },
});

// Request interceptor
apiClient.interceptors.request.use(
  (config) => {
    // Add auth token if available
    let token = localStorage.getItem('access_token') || localStorage.getItem('authToken');
    
    // In development mode, use dev token if no token is available
    if (!token && import.meta.env.DEV) {
      token = 'dev_token_123';
      console.log('[DEV MODE] Using development token for API requests');
    }
    
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    
    // Ensure UTF-8 encoding for all requests
    config.headers['Content-Type'] = 'application/json; charset=utf-8';
    config.headers['Accept'] = 'application/json; charset=utf-8';
    
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor
apiClient.interceptors.response.use(
  (response: AxiosResponse) => {
    return response;
  },
  (error) => {
    // Handle common errors
    if (error.response?.status === 401) {
      // Unauthorized - redirect to login
      localStorage.removeItem('access_token');
      localStorage.removeItem('authToken');
      localStorage.removeItem('reprotech_auth_user');
      localStorage.removeItem('userData');
      
      // In development, show a helpful message
      if (import.meta.env.DEV) {
        console.warn('Authentication token expired or invalid');
      }
      
      // Redirect to login page
      window.location.href = '/login';
    }
    
    return Promise.reject(error);
  }
);

// Generic CRUD functions factory
export interface CrudFunctions<T, TCreate = Partial<T>, TUpdate = Partial<T>> {
  getAll: () => Promise<T[]>;
  getById: (id: string | number) => Promise<T>;
  create: (data: TCreate) => Promise<T>;
  update: (id: string | number, data: TUpdate) => Promise<T>;
  delete: (id: string | number) => Promise<void>;
}

export function createCrudFunctions<T, TCreate = Partial<T>, TUpdate = Partial<T>>(
  endpoint: string
): CrudFunctions<T, TCreate, TUpdate> {
  return {
    getAll: async (): Promise<T[]> => {
      const response = await apiClient.get(`/${endpoint}`);
      return response.data;
    },

    getById: async (id: string | number): Promise<T> => {
      const response = await apiClient.get(`/${endpoint}/${id}`);
      return response.data;
    },

    create: async (data: TCreate): Promise<T> => {
      const response = await apiClient.post(`/${endpoint}`, data);
      return response.data;
    },

    update: async (id: string | number, data: TUpdate): Promise<T> => {
      const response = await apiClient.put(`/${endpoint}/${id}`, data);
      return response.data;
    },

    delete: async (id: string | number): Promise<void> => {
      await apiClient.delete(`/${endpoint}/${id}`);
    },
  };
}

export default apiClient;
